import React, { useCallback, useEffect, useState } from 'react';
import { Meeting, PersonaBriefing } from '../../../../types/meeting';
import meetingsAPI from '../../../../services/meetingsApi';

interface MeetingBriefingPanelProps {
  meeting: Meeting;
  /** Allows the panel to be collapsed via its header (used on multi-card pages). */
  collapsible?: boolean;
  onError?: (message: string) => void;
}

const MeetingBriefingPanel: React.FC<MeetingBriefingPanelProps> = ({ meeting, collapsible = false, onError }) => {
  const [guidance, setGuidance] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [briefing, setBriefing] = useState<PersonaBriefing | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const isCollapsed = collapsible && collapsed;

  const meetingId = meeting.meeting_id;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    meetingsAPI
      .getPersonaBriefing(meetingId)
      .then((b) => {
        if (!cancelled) setBriefing(b);
      })
      .catch(() => {
        if (!cancelled) setBriefing(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    try {
      const b = await meetingsAPI.generatePersonaBriefing(meetingId, guidance.trim() || undefined);
      setBriefing(b);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to generate briefing');
    } finally {
      setIsGenerating(false);
    }
  }, [meetingId, guidance, onError]);

  return (
    <div className={`meeting-panel${isCollapsed ? ' meeting-panel-collapsed' : ''}`}>
      <div
        className={`meeting-panel-header${collapsible ? ' meeting-panel-header-collapsible' : ''}`}
        role={collapsible ? 'button' : undefined}
        aria-expanded={collapsible ? !collapsed : undefined}
        onClick={collapsible ? () => setCollapsed((c) => !c) : undefined}
      >
        <div>
          <h3 className="meeting-panel-title">Your Briefing &amp; Insights</h3>
          {briefing?.generated_at && (
            <p className="meeting-panel-subtitle">
              Tailored to your custom instructions · Generated {new Date(briefing.generated_at).toLocaleString()}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            className="meeting-btn meeting-btn-ghost meeting-btn-sm"
            onClick={(e) => { e.stopPropagation(); handleGenerate(); }}
            disabled={isGenerating || loading}
          >
            {briefing ? 'Regenerate' : 'Generate Briefing'}
          </button>
          {collapsible && (
            <span className={`meeting-panel-chevron ${collapsed ? '' : 'expanded'}`}>▾</span>
          )}
        </div>
      </div>

      {isCollapsed ? null : loading ? (
        <div className="meeting-generating">
          <span className="spinner" /> Loading your briefing...
        </div>
      ) : isGenerating ? (
        <div className="meeting-generating">
          <span className="spinner" /> Writing your briefing from the materials...
        </div>
      ) : !briefing ? (
        <div className="meeting-empty-hint">
          No briefing yet. Generate a concise pre-read grounded in the uploaded materials and agenda,
          tailored to your custom instructions — or add optional guidance below first.
        </div>
      ) : (
        <div className="meeting-text-block">{briefing.content}</div>
      )}

      {!isCollapsed && !loading && !isGenerating && (
        <textarea
          className="meeting-form-input"
          style={{ marginTop: 10, width: '100%', minHeight: 60, resize: 'vertical' }}
          placeholder="Optional guidance for this briefing, e.g. 'emphasize financial exposure and open legal questions'"
          value={guidance}
          onChange={(e) => setGuidance(e.target.value)}
        />
      )}
    </div>
  );
};

export default MeetingBriefingPanel;
